import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  BarChart3, PieChart, TrendingUp, Package2, Loader2, ArrowLeft,
  Ruler, Tag, Palette, Layers, Calendar, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Statistics() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat(i18n.language === 'fi' ? 'fi-FI' : 'en-US').format(num);
  };

  // Color palette for charts
  const COLORS = [
    'bg-primary', 'bg-secondary', 'bg-accent', 'bg-blue-500', 'bg-purple-500',
    'bg-pink-500', 'bg-orange-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500'
  ];

  const getMaxCount = (items) => {
    if (!items?.length) return 1;
    return Math.max(...items.map(i => i.count));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isEnglish ? 'Back to Dashboard' : 'Takaisin Etusivulle'}
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            {isEnglish ? 'Stash Statistics' : 'Varastotilastot'}
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            {isEnglish ? 'Insights about your yarn collection' : 'Näkemyksiä lankakokoelmastasi'}
          </p>
        </div>

        {!stats || stats.total_skeins === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
            <BarChart3 className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">
              {isEnglish ? 'No data yet' : 'Ei vielä tietoja'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {isEnglish 
                ? 'Add some yarn to your stash to see statistics' 
                : 'Lisää lankoja varastoosi nähdäksesi tilastot'}
            </p>
            <Button onClick={() => navigate('/inventory/add')} className="rounded-full">
              {isEnglish ? 'Add Your First Yarn' : 'Lisää Ensimmäinen Lanka'}
            </Button>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Package2 className="h-6 w-6 text-primary" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-primary">
                    {formatNumber(stats.total_skeins)}
                  </span>
                </div>
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Total Skeins' : 'Kerät Yhteensä'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? 'In your stash' : 'Varastossasi'}
                </p>
              </div>

              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Ruler className="h-6 w-6 text-secondary" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-secondary">
                    {formatNumber(stats.total_yardage)}
                  </span>
                </div>
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Total Yardage' : 'Juoksu Yhteensä'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? 'Yards estimated' : 'Jaardia arvioitu'}
                </p>
              </div>

              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <Layers className="h-6 w-6 text-accent" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-accent">
                    {formatNumber(stats.unique_yarns)}
                  </span>
                </div>
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Unique Yarns' : 'Eri Lankoja'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? 'Different entries' : 'Eri merkintöjä'}
                </p>
              </div>

              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Activity className="h-6 w-6 text-blue-500" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-blue-500">
                    {formatNumber(stats.usage_stats?.total_used || 0)}
                  </span>
                </div>
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Skeins Used' : 'Käytetyt Kerät'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isEnglish 
                    ? `In ${stats.usage_stats?.projects_count || 0} projects` 
                    : `${stats.usage_stats?.projects_count || 0} projektissa`}
                </p>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Brands Chart - Horizontal Bar */}
              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Tag className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-heading font-bold">
                    {isEnglish ? 'Top Brands' : 'Suosituimmat Merkit'}
                  </h2>
                </div>
                {stats.brands?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.brands.slice(0, 8).map((brand, index) => (
                      <div key={brand.name} className="flex items-center gap-3">
                        <span className="w-24 text-sm text-muted-foreground truncate" title={brand.name}>
                          {brand.name}
                        </span>
                        <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${COLORS[index % COLORS.length]} rounded-full transition-all duration-500`}
                            style={{ width: `${(brand.count / getMaxCount(stats.brands)) * 100}%` }}
                          />
                        </div>
                        <span className="w-10 text-sm font-medium text-right">{brand.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {isEnglish ? 'No brand data' : 'Ei merkkitietoja'}
                  </p>
                )}
              </div>

              {/* Weight Distribution - Pie/Donut Style */}
              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center">
                    <PieChart className="h-5 w-5 text-secondary" />
                  </div>
                  <h2 className="text-xl font-heading font-bold">
                    {isEnglish ? 'By Weight' : 'Paksuuden Mukaan'}
                  </h2>
                </div>
                {stats.weights?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {stats.weights.map((weight, index) => {
                      const percentage = Math.round((weight.count / stats.total_skeins) * 100);
                      return (
                        <div 
                          key={weight.name}
                          className={`px-4 py-2 rounded-full text-white text-sm font-medium ${COLORS[index % COLORS.length]}`}
                          style={{ minWidth: `${Math.max(percentage * 2, 60)}px` }}
                        >
                          {weight.name} ({weight.count})
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {isEnglish ? 'No weight data' : 'Ei paksuustietoja'}
                  </p>
                )}
              </div>

              {/* Fiber Types */}
              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <Layers className="h-5 w-5 text-accent" />
                  </div>
                  <h2 className="text-xl font-heading font-bold">
                    {isEnglish ? 'Fiber Types' : 'Kuitutyypit'}
                  </h2>
                </div>
                {stats.fibers?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.fibers.slice(0, 8).map((fiber, index) => (
                      <div key={fiber.name} className="flex items-center gap-3">
                        <span className="w-28 text-sm text-muted-foreground truncate" title={fiber.name}>
                          {fiber.name}
                        </span>
                        <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${COLORS[index % COLORS.length]} rounded-full transition-all duration-500`}
                            style={{ width: `${(fiber.count / getMaxCount(stats.fibers)) * 100}%` }}
                          />
                        </div>
                        <span className="w-10 text-sm font-medium text-right">{fiber.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {isEnglish ? 'No fiber data' : 'Ei kuitutietoja'}
                  </p>
                )}
              </div>

              {/* Colors */}
              <div className="rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Palette className="h-5 w-5 text-purple-500" />
                  </div>
                  <h2 className="text-xl font-heading font-bold">
                    {isEnglish ? 'Top Colors' : 'Suosituimmat Värit'}
                  </h2>
                </div>
                {stats.colors?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {stats.colors.slice(0, 10).map((color, index) => (
                      <div 
                        key={color.name}
                        className="flex items-center justify-between px-3 py-2 rounded-xl bg-stone-50"
                      >
                        <span className="text-sm truncate" title={color.name}>{color.name}</span>
                        <span className="ml-2 text-sm font-medium text-muted-foreground">{color.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {isEnglish ? 'No color data' : 'Ei väritietoja'}
                  </p>
                )}
              </div>

              {/* Monthly Additions - Line/Bar Chart */}
              {stats.monthly_additions?.length > 0 && (
                <div className="lg:col-span-2 rounded-3xl bg-white border border-stone-100 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-500" />
                    </div>
                    <h2 className="text-xl font-heading font-bold">
                      {isEnglish ? 'Monthly Additions' : 'Kuukausittaiset Lisäykset'}
                    </h2>
                  </div>
                  <div className="h-48 flex items-end justify-between gap-2">
                    {stats.monthly_additions.map((month, index) => {
                      const maxMonthly = Math.max(...stats.monthly_additions.map(m => m.count));
                      const height = maxMonthly > 0 ? (month.count / maxMonthly) * 100 : 0;
                      const monthLabel = new Date(month.month + '-01').toLocaleDateString(
                        i18n.language === 'fi' ? 'fi-FI' : 'en-US', 
                        { month: 'short' }
                      );
                      return (
                        <div key={month.month} className="flex-1 flex flex-col items-center">
                          <span className="text-xs font-medium mb-1">{month.count}</span>
                          <div 
                            className="w-full bg-primary rounded-t-lg transition-all duration-500"
                            style={{ height: `${Math.max(height, 4)}%` }}
                          />
                          <span className="text-xs text-muted-foreground mt-2">{monthLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
